export const defaultColorPalette = {
  '--color-primary-1': '#334d97',
  '--color-primary-2': '#5888cf',
  '--color-primary-3': '#d6e6ff',
  '--color-secondary-1': '#ffa914',
  '--color-secondary-2': '#fecd6e',
  '--color-secondary-3': '#ffefcf',
  '--color-grey-1': '#6f757e',
  '--color-grey-2': '#9fa3a9',
  '--color-grey-3': '#cfd1d4',
  '--color-grey-4': '#f6f6f6',
  '--color-grey-5': '#F0F2F5',
  '--color-system-1': '#0d1a43',
  '--color-system-2': '#0f1928',
  '--color-system-3': '#f6521e',
  '--color-system-4': '#fff',
  '--color-system-5': '#3ebb61',
}

export type ColorPalette = typeof defaultColorPalette

export const buildThemeFromPalette = (palette: ColorPalette): any => ({
  token: {
    colorPrimary: palette['--color-primary-1'],
    info: palette['--color-primary-2'],
    colorSuccess: palette['--color-system-5'],
    colorError: palette['--color-system-3'],

    colorBgBase: '#FFFFFF',
    colorBgContainer: '#FFFFFF',
    colorBgLayout: '#FFFFFF',
    colorBgElevated: '#FFFFFF',
    colorBgHeader: palette['--color-system-1'],

    colorBorder: palette['--color-grey-3'],
    colorBorderSecondary: '#EDEDED',

    colorTextLabel: palette['--color-grey-1'],
    colorTextPlaceholder: palette['--color-grey-1'],
    colorTextDisabled: palette['--color-grey-3'],

    colorFillAlter: '#F5F7FA',
    colorFillSecondary: palette['--color-primary-3'],

    colorTextLightSolid: '#FFFFFF',

    marginLG: 24,
    fontWeightStrong: 700,
  },
  components: {
    Layout: {
      siderBg: palette['--color-system-1'],
      headerBg: palette['--color-system-1'],
      bodyBg: palette['--color-grey-4'],
    },
    Menu: {
      itemBg: palette['--color-system-1'],
      itemText: '#FFFFFF',
      itemTextHover: palette['--color-system-1'],
      itemTextSelected: palette['--color-secondary-1'],

      itemBgSelected: palette['--color-system-1'],
      itemBgHover: palette['--color-system-1'],

      colorSubBg: palette['--color-system-1'],

      itemColor: '#FFFFFF',
      itemHoverColor: palette['--color-system-1'],
      itemSelectedColor: palette['--color-secondary-1'],
      itemSelectedBg: palette['--color-system-1'],
      itemSelectedTextColor: palette['--color-secondary-1'],
    },
    Table: {
      headerBg: '#FFFFFF',
      headerColor: '#0F1928',
      rowHoverBg: '#F5F7FA',
      rowSelectedBg: 'transparent',
      borderColor: palette['--color-grey-3'],
      sortIconColor: palette['--color-primary-2'],
      bodySortBg: '#FFFFFF',
      headerSortActiveBg: '#FFFFFF',
      fixedHeaderSortActiveBg: '#FFFFFF',
    },
    Button: {
      colorPrimary: palette['--color-primary-1'],
      colorPrimaryHover: palette['--color-primary-2'],
      colorTextLightSolid: '#FFFFFF',
      colorBorder: '#EDEDED',
      defaultBg: '#FFFFFF',
      defaultColor: '#0F1928',
      defaultBorderColor: palette['--color-grey-3'],
    },
    Input: {
      colorTextPlaceholder: palette['--color-grey-1'],
      colorBorder: palette['--color-grey-3'],
      activeBorderColor: palette['--color-primary-1'],
    },
    Select: {
      colorTextPlaceholder: palette['--color-grey-1'],
      optionSelectedBg: palette['--color-primary-3'],
    },
    Tag: {
      defaultBg: '#F5F7FA',
      defaultColor: '#0F1928',
    },
    DatePicker: {
      cellActiveWithRangeBg: palette['--color-grey-3'],
      cellHoverWithRangeBg: palette['--color-grey-3'],
      cellRangeBorderColor: palette['--color-grey-3'],
    },
    Form: {
      itemMarginBottom: 16,
    },
    Switch: {
      trackMinWidthSM: 32,
    },
    Card: {
      headerFontSize: 20,
      headerFontWeight: 700,
    },
  },
})

const root = document.querySelector(':root') as HTMLElement

class ColorPaletteManager {
  normalize = (palette: Partial<ColorPalette>): ColorPalette => {
    return (Object.entries(defaultColorPalette) as [keyof ColorPalette, string][]).reduce(
      (acc, [key, defaultValue]) => {
        const raw = palette && palette[key]
        const value = typeof raw === 'string' ? raw.trim() : raw
        ;(acc as any)[key] = (value ? value : defaultValue) as string
        return acc
      },
      {} as ColorPalette,
    )
  }

  get currentPalette(): ColorPalette {
    const fromDom: Partial<ColorPalette> = {}
    ;(Object.keys(defaultColorPalette) as Array<keyof ColorPalette>).forEach((key) => {
      ;(fromDom as any)[key] = root.style.getPropertyValue(key)
    })
    return this.normalize(fromDom)
  }

  update = (colorPalette: ColorPalette, htmlElement: HTMLElement = root) => {
    Object.entries(colorPalette).forEach(([key, value]) => htmlElement.style.setProperty(key, value))
  }

  reset = (htmlElement: HTMLElement = root) => {
    this.update(defaultColorPalette, htmlElement)
  }
}

export const colorPaletteManager = new ColorPaletteManager()
